import { SanEngineManager } from "../../../engine/SanEngineManager";
import { RevealerManager } from "../../../modules/revealer/RevealerManager";
import { Revealer } from "../../../modules/revealer/Revealer";
import { StateHandler } from "../../../../core/utils/StateHandler";
import { Presentation } from "../../../../core/gameflow/controller/Presentation";
import { GameData } from "../../../server/data/GameData";
import { SanQuentin2 } from "../../../SanQuentin2";
import { IBetWayWinData } from "../../../server/base/IData";
import { GameSetting } from "../../../../core/setting/GameSetting";
import { SoundManager } from "../../../config/sound/SoundManager";
import { SoundConfig } from "../../../config/sound/SoundConfig";
import { GameMode } from '@nolimitcity/slot-game/bin/flowcontrol/GameMode';
import { GameConfig } from "../../../config/base/GameConfig";
import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { SanSpriteSymbol } from "../../../engine/Symbol/SanSpriteSymbol";
import { Speedster } from "../../../../core/debug/Speedster";
import { MathHelper } from "../../../../core/utils/MathHelper";

enum WinState {
    WIN,
    NORMAL,
    DONE,
}

export class NormalWinPresentation extends Presentation {

    private state = new StateHandler(undefined, new Map<WinState, Function>([
        [WinState.WIN, (skip: Function) => this.winPositions(skip)],
        [WinState.NORMAL, (skip: Function) => this.addWinCountUpToTimeline(skip)],
        [WinState.DONE, (skip: Function) => this.onEnd(skip)],
    ]));

    private winAmount: number = 0;
    private skipped: boolean = false;
    private isBigWin: boolean = false;
    private winIndx: number = 0;

    private winConfigDurr: number = 0;
    private lastNormalIndex: number = 0;

    private isBelowStake: boolean = false;
    private isTotalWin: boolean = false;
    private ignoreTotal: boolean = false;

    /**
     * Checks if the win presentation should be activated based on the provided GameData.
     */
    public checkForActivation(data: GameData): boolean { 
        if (GameSetting.isDevMode && Speedster.ignoreWins) return false;
        return !!data.Win.singleWin;
    }

    /**
     * Initializes the presentation with necessary configurations.
     */
    public initialization(): void {
        this.lastNormalIndex = GameConfig.WIN_RATIOS.normalWin.length - 1;
    }

    /**
     * Sets the game data and computes necessary win configurations.
     */
    public setGameData(data: GameData): void {
        super.setGameData(data);

        this.isBigWin = this._gameData.Win.isBigWin;
        this.isTotalWin = this._gameData.Win.shouldShowTotalWin;
        this.winAmount = !this.isBigWin
            ? this._gameData.Win.singleWin
            : this._gameData.Win.playedBetValue * GameConfig.WIN_RATIOS.bigWin[0].ratio;

        this.winIndx = GameConfig.WIN_RATIOS.normalWin.findIndex(indx => indx.ratio === this._gameData.Win.winRatioConfig.ratio);
        if (this.winIndx === -1) this.winIndx = this.lastNormalIndex;

        this.winConfigDurr = GameConfig.WIN_RATIOS.normalWin[this.winIndx].duration;
        this.isBelowStake = data.Win.isWinBelowStake!() && this._gameData.isNormalMode;

        this.ignoreTotal = this.isBigWin || 
            this.isBelowStake || 
            (!this._gameData.Win.winRatioConfig.ratio && this._gameData.Win.betWayWins.length == 1);
    }

    /**
     * Prepares the win state for the start of the presentation.
     */
    public preStart(): void {
        const bigWinGrps = this.isBigWin && this._gameData.Win.betWayWins.length > 1;

        if(bigWinGrps || !this.isBigWin){
            this.state.set(WinState.WIN);
        }else
            this.state.set(WinState.DONE);

        this.skipped = false;
    }

    /**
     * Creates the win presentation based on the current state.
     */

    public createPresentation(skip: Function): void {
        this.state.triggerState(skip);
    }

    /**
     * Handles the win positions and animations during the presentation.
     */
    protected winPositions(skip: Function): void {
        skip(true);
        const winGrps: IBetWayWinData[] = this._gameData.Win.betWayWins;
        this.state.set(this.ignoreTotal ? WinState.DONE : WinState.NORMAL);
        if (!this.ignoreTotal && this.winIndx && winGrps.length === 1 && !GameSetting.showAllIndiviWins) {
            return;
        }

        const animationDurr: number = GameSetting.isFastSpin ? 0.3 : 0.5;
        const animationReelOffset: number = animationDurr/10; // GameSetting.isFastSpin ? 0.04 : 0.055;
        const animationGroupOffset: number = animationDurr/2; // GameSetting.isFastSpin ? 0.2 : 0.275;
        let triggerTime: number = 0;

        winGrps.forEach((winData, groups) => {
            let showMiniText: boolean = winGrps.length !== 1 || !this.winIndx;// || this.ignoreTotal;
          
            this.timeline.add(() => {
                if (!this.skipped && !this.isBelowStake) { 
                    SoundManager.playSoundEffect(SoundConfig.HIGHLIGHTWIN_COMMON)
                }
            }, triggerTime);
            
            winData.reelWinPositions.forEach((posRow, reel) => {
                const callBacks: Function[] = [];
                const hintX:boolean = reel === MathHelper.clamp(winData.length - 1,2,3);
                let hintYIndx:number = findClosestTrueIndex(posRow);
                if(posRow[hintYIndx-1]) hintYIndx--;
          
                posRow.forEach((isAWin, row) => {
                    const sym: SanSpriteSymbol | undefined = SanEngineManager.getReel(reel).getSymbol(row - 1);
                    if ((row === 0) || (row === posRow.length - 1)) {
                        const bar: Revealer = row === 0 ? RevealerManager.getTopBar(reel) : RevealerManager.getBotBar(reel);
                        if (bar.isActive) {
                            callBacks.push(() => { bar.winAnimation(isAWin, animationDurr, winData.winLvl); });
                            if (showMiniText && hintX && row == hintYIndx && isAWin && !(this.isBelowStake && this._gameData.isNormalMode)) {
                                callBacks.push(() => { bar.hintValue(winData.multipliedAmount, winData.numberOfCombinations); });
                                showMiniText = false;
                            }
                        }
                    } else if (sym) {
                        callBacks.push(() => { sym.playWin(isAWin, animationDurr, winData.winLvl); });
                        if (showMiniText && hintX && row == hintYIndx && isAWin && !(this.isBelowStake && this._gameData.isNormalMode)) {
                            callBacks.push(() => { sym.hintValue(winData.multipliedAmount, winData.numberOfCombinations); });
                            showMiniText = false;
                        }
                    }
                });

                callBacks.forEach(cb => this.timeline.add(() => cb(), triggerTime));
                if (reel <= winData.length - 1) {
                    triggerTime += animationReelOffset;
                }
            });

            if (groups !== winGrps.length - 1) {
                triggerTime += animationGroupOffset;
            }
        });

        function findClosestTrueIndex(arr: boolean[]): number {
            const middleIndex = Math.floor(arr.length / 2);
            let closestIndex = -1;
            let minDistance = Infinity;
        
            for (let i = 0; i < arr.length; i++) {
                if (arr[i] === true) {
                    const distance = Math.abs(i - middleIndex);
                    if (distance < minDistance) {
                        minDistance = distance;
                        closestIndex = i;
                    }
                }
            }
            return closestIndex;
        }
        
        this.timeline.to(this, { duration: triggerTime + animationDurr/(this.isBigWin ? 4 : 2) }, 0);
    }

    /**
     * Adds the win count up animation to the timeline.
     */
    protected addWinCountUpToTimeline(skip: Function): void {
        skip(true);
        
        const countUpDuration: number = this.winConfigDurr;
        const animDuration: number = countUpDuration*1.5;

        this.timeline.add(SanQuentin2.counter.play(0, this.winAmount, this.winIndx != 0 ?  countUpDuration : 0));
        if(this.winIndx != 0){
            SoundManager.playSoundEffect(SoundConfig.WIN_COUNT,Infinity)
            SoundManager.playSoundEffect(SoundConfig.HIGHLIGHTWIN_LVL[MathHelper.clamp(this.winIndx-1,0, SoundConfig.HIGHLIGHTWIN_LVL.length-1) ]);
        }
        
        this.timeline.add(() => {
            SoundManager.stopSoundEffect(SoundConfig.WIN_COUNT);
            SanQuentin2.overlay.winLvlAnimation(this.winIndx);
            SanQuentin2.counter.bounceHit(this.winIndx);
            SoundManager.playSoundEffect(SoundConfig.WIN_LVL[this.winIndx]);
        });
        
        this.timeline.add(SanQuentin2.counter.showWays(this.winIndx != 0 ? 0.5 : 0, this._gameData.Win.allBetWayWinData.totalWinCombination));

        const lenDur: number = 0;

        this._gameData.Reel.reels.forEach((_, reel) => {
            const posRow = this._gameData.Win.allBetWayWinData.reelWinPositions[reel];
            posRow.forEach((isAWin, row) => {
                const sym = SanEngineManager.getReel(reel).getSymbol(row - 1);
                if (isAWin && (row === 0 || row === posRow.length - 1)) {
                    const bar = row === 0 ? RevealerManager.getTopBar(reel) : RevealerManager.getBotBar(reel);
                    if (bar.isActive) {
                        this.timeline.add(() => { bar.winAnimation(isAWin, animDuration*2, this.winIndx); }, lenDur * reel);
                    }
                } else if (sym) {
                    this.timeline.add(() => { sym.playWin(isAWin, animDuration*2, this.winIndx); }, lenDur * reel);
                }
            });
        });

        // SoundManager.playSoundEffect(SoundConfig.HIGHLIGHTWIN_LVL[this.winIndx]);

        this.timeline.to(this, {duration:countUpDuration}, 0);

        this.state.set(WinState.DONE);
    }

    /**
     * Handles the end of the win presentation.
     */
    private onEnd(skip: Function): void {
        this.setPlayable(false);
    }

    /**
     * Finalizes the presentation, handling any necessary win updates.
     */
    public done(): void {
        if (!this.isBigWin) {
            SlotGame.keypadPlugin.setWin(this._gameData.Win!.totalWin, this.isTotalWin, this._gameData.Win.isWinBelowStake!());
            if (this.skipped) {
                SanQuentin2.counter.stop(this.winAmount, 0.25, 0.25);
            } else {
                SanQuentin2.counter.stop(this.winAmount, 0.5, 1);
            }
        }

        if (this.isBigWin && this._gameData.Win.isWinBelowStake!()) {
            SlotGame.keypadPlugin.setWin(this._gameData.Win!.totalWin, this.isTotalWin, this._gameData.Win.isWinBelowStake!());
        }

        SanEngineManager.RSAll((r, s) => { s.markNonWin(false, undefined, 1); });
        RevealerManager.getAllBars().forEach(rev => rev.markNonWin(false, undefined, 1));
    }

    /**
     * Skips the win presentation.
     */
    public skip(): void {
        this.skipped = true;
        super.skip();
    }
}
